// API
import { updateNotification } from "api/notifications";

// Routes
import { PROTECTED_ROUTES } from "routes";

/**
 * Marks a notification as read if it's not already read
 * @param {Object} notification - The notification object
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} switchToReadAction - Redux action to switch notification to read
 * @param {Function} switchToUnreadAction - Redux action to switch notification to unread (for error handling)
 * @param {Function} setErrorAction - Redux action to set error message
 */
export const markNotificationAsRead = async (
  notification,
  dispatch,
  switchToReadAction,
  switchToUnreadAction,
  setErrorAction
) => {
  if (!notification.is_read) {
    try {
      // Optimistic update
      dispatch(switchToReadAction(notification.id));

      // Make API call
      await updateNotification(notification.id, { is_read: true });
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      // Revert optimistic update on error
      if (switchToUnreadAction) {
        dispatch(switchToUnreadAction(notification.id));
      }
      dispatch(setErrorAction("Failed to mark notification as read"));
    }
  }
};

// Helper function to handle navigation after marking as read
const navigateAfterMarkingRead = async (
  notification,
  navigate,
  markAsReadFn,
  onClose,
  route
) => {
  await markAsReadFn(notification);
  if (onClose) onClose();
  navigate(route);
};

// Handler for SubTask notifications
const handleSubTaskNotification = async (
  notification,
  navigate,
  markAsReadFn,
  onClose
) => {
  const { serialized_target_model_data, target_id } = notification;
  const company_id = serialized_target_model_data?.task?.service?.client;

  if (!company_id) return null;

  await navigateAfterMarkingRead(
    notification,
    navigate,
    markAsReadFn,
    onClose,
    PROTECTED_ROUTES.SUBTASK(company_id, target_id)
  );
  return true;
};

// Handler for Service notifications
const handleServiceNotification = async (
  notification,
  navigate,
  markAsReadFn,
  onClose
) => {
  const { serialized_target_model_data, target_id } = notification;
  const company_id = serialized_target_model_data?.client;

  if (!company_id) return null;

  await navigateAfterMarkingRead(
    notification,
    navigate,
    markAsReadFn,
    onClose,
    PROTECTED_ROUTES.SERVICE(company_id, target_id)
  );
  return true;
};

// Handler for Task notifications
const handleTaskNotification = async (
  notification,
  navigate,
  markAsReadFn,
  onClose
) => {
  const { serialized_target_model_data, target_id } = notification;
  const company_id = serialized_target_model_data?.service?.client;

  if (!company_id) return null;

  await navigateAfterMarkingRead(
    notification,
    navigate,
    markAsReadFn,
    onClose,
    PROTECTED_ROUTES.TASK(company_id, target_id)
  );
  return true;
};

// Handler for the reply of Marketing Mind to an operator's feedback: the
// «Feedback a Marketing Mind» section of the client's «Marketing Mind v2» tab
const handleMarketingMindFeedbackNotification = async (
  notification,
  navigate,
  markAsReadFn,
  onClose
) => {
  const company_id = notification.serialized_target_model_data?.company;

  if (!company_id) return null;

  await navigateAfterMarkingRead(
    notification,
    navigate,
    markAsReadFn,
    onClose,
    `${PROTECTED_ROUTES.CLIENT_MARKETING_MIND_V2(company_id)}?section=feedback`
  );
  return true;
};

// Handler for Marketing Mind proposal notifications: the proposals are on the
// client's «Marketing Mind v2» tab
const handleMarketingMindProposalNotification = async (
  notification,
  navigate,
  markAsReadFn,
  onClose
) => {
  const company_id = notification.serialized_target_model_data?.company;

  if (!company_id) return null;

  await navigateAfterMarkingRead(
    notification,
    navigate,
    markAsReadFn,
    onClose,
    PROTECTED_ROUTES.CLIENT_MARKETING_MIND_V2(company_id)
  );
  return true;
};

// Handler for Comment notifications
const handleCommentNotification = async (
  notification,
  navigate,
  markAsReadFn,
  onClose
) => {
  const { serialized_target_model_data } = notification;
  const target_model_name = serialized_target_model_data?.target_model_name;
  const content_type_object_id = serialized_target_model_data?.content_type_object_id;
  const comment_target_data = serialized_target_model_data?.serialized_target_model_data;

  if (!target_model_name || !content_type_object_id || !comment_target_data) {
    return null;
  }

  const commentHandlers = {
    subtask: () => ({
      company_id: comment_target_data?.task?.service?.client,
      route: PROTECTED_ROUTES.SUBTASK(comment_target_data?.task?.service?.client, content_type_object_id),
    }),
    task: () => ({
      company_id: comment_target_data?.service?.client,
      route: PROTECTED_ROUTES.TASK(comment_target_data?.service?.client, content_type_object_id),
    }),
    service: () => ({
      company_id: comment_target_data?.client,
      route: PROTECTED_ROUTES.SERVICE(comment_target_data?.client, content_type_object_id),
    }),
  };

  const handler = commentHandlers[target_model_name];
  if (!handler) {
    console.log("Unknown comment target model:", target_model_name);
    return null;
  }

  const { company_id, route } = handler();
  if (!company_id) return null;

  await navigateAfterMarkingRead(notification, navigate, markAsReadFn, onClose, route);
  return true;
};

/**
 * Handles notification click - marks as read and navigates to appropriate route
 * @param {Object} notification - The notification object
 * @param {Function} navigate - React Router navigate function
 * @param {Function} markAsReadFn - Function to mark notification as read
 * @param {Function} onClose - Optional callback to close popover/modal
 */
export const handleNotificationClick = async (
  notification,
  navigate,
  markAsReadFn,
  onClose = null
) => {
  try {
    const { target_model_data, serialized_target_model_data } = notification;

    if (!target_model_data || !serialized_target_model_data) {
      console.log("Notification missing required data:", notification);
      return;
    }

    const model = target_model_data.model;
    const modelHandlers = {
      SubTask: handleSubTaskNotification,
      Service: handleServiceNotification,
      Task: handleTaskNotification,
      Comment: handleCommentNotification,
      MarketingMindProposal: handleMarketingMindProposalNotification,
      MarketingMindOperatorFeedback: handleMarketingMindFeedbackNotification,
    };

    const handler = modelHandlers[model];
    if (!handler) {
      console.log("Unknown notification model type:", model);
      return;
    }

    const result = await handler(notification, navigate, markAsReadFn, onClose);
    if (result === null) {
      console.log("Could not extract company ID from notification:", notification);
    }
  } catch (error) {
    console.error("Error handling notification click:", error);
  }
};
